import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";
import styled from "styled-components";
import useScrollLock from "../hooks/useScrollLock";
import {
  selectAddRoleToUserPending,
  selectBusinessUserRoles,
} from "../store/slices/businessUser/slice";
import { useSelector } from "react-redux";
import { ReactComponent as LoadingSvg } from "../assets/icons/loader-white.svg";

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 1.2rem;
  font-family: "Avenir-Medium";
  width: 380px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.25);
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: #484888;
  font-family: "Avenir-Heavy";
  font-size: 1.6rem;
`;

const RoleItem = styled.div`
  padding: 12px 16px;
  border-radius: 0.8rem;
  cursor: pointer;
  transition: 0.2s;
  border: 1px solid transparent;
  font-size: 1.4rem;
  border-color: ${(props) => (props.$selected ? "#484888" : "#48488850")};
  background: ${(props) => (props.$selected ? "#484888" : "transparent")};
  color: ${(props) => (props.$selected ? "#fff" : "#000")};
  text-transform: ${(props) => (props.$capitalize ? "capitalize" : "unset")};

  &:hover {
    padding-left: 1rem;
    border-color: #484888;
    background: ${(props) =>
      props.$selected ? "#484888" : "rgba(72, 72, 136, 0.06)"};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const Button = styled.button`
  border-radius: 30px;
  user-select: none;
  outline: none;
  background: ${(props) => (props.$variant === "cancel" ? "#fff" : "#484888")};
  padding: 10px 30px;
  align-items: center;
  justify-content: center;
  display: flex;
  border: ${(props) =>
    props.$variant === "cancel" ? "1px solid #484888" : "none"};
  white-space: nowrap;
  color: ${(props) => (props.$variant === "cancel" ? "#484888" : "#fff")};
  text-align: center;
  font-family: "Avenir";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.08px;
  box-shadow: 0px 12px 24px -6px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  flex: 1;

  &:active {
    transition: all 0.2s ease-in-out;
    scale: 0.95;
  }

  @media (max-width: 990px) {
    font-size: 16px;
  }
  @media (max-width: 952px) {
    font-size: 14px;
  }
  @media (max-width: 915px) {
    font-size: 12px;
  }
  @media (max-width: 795px) {
    font-size: 11px;
  }

  &:disabled {
    cursor: not-allowed;
    scale: 1 !important;
    background-color: #7f7fac;
    opacity: 0.65;
  }
`;

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    justifyContent: "center",
    backdropFilter: "blur(5px)",
    alignItems: "center",
    animation: "fadeIn 0.25s ease",
  },
  content: {
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "scaleIn 0.25s ease",
  },
};

export const LoadingIcon = styled(LoadingSvg)`
  circle {
    fill: white;
  }
`;

// Keyframes
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `
@keyframes fadeIn {
  from { opacity: 0; } 
  to { opacity: 1; }
}`,
  styleSheet.cssRules.length
);
styleSheet.insertRule(
  `
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; } 
  to { transform: scale(1); opacity: 1; }
}`,
  styleSheet.cssRules.length
);

const RoleModal = ({ isOpen, onClose, onSelectRole, businessUser }) => {
  const businessUserRoles = useSelector(selectBusinessUserRoles);
  const [formattedRoles, setFormattedRoles] = useState([]);
  const addRoleToUserPending = useSelector(selectAddRoleToUserPending);

  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState(null);

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      if (businessUser && businessUser.role) {
        const existRole = businessUserRoles.data.find(
          (role) => role.name === businessUser.role
        );
        if (existRole) {
          setSelectedRole(existRole);
        }
      }
    }
  }, [businessUser, businessUserRoles, isOpen]);

  useEffect(() => {
    if (businessUserRoles.data?.length && businessUser?.role) {
      const roles = [...businessUserRoles.data];
      const index = roles.findIndex((role) => role.name === businessUser.role);

      if (index > -1) {
        const [userRole] = roles.splice(index, 1);
        roles.unshift(userRole);
      }
      setFormattedRoles(roles);
    }
  }, [businessUserRoles.data, businessUser?.role]);

  const handleSave = () => {
    if (selectedRole) {
      onSelectRole(selectedRole.id, businessUser.id);
    }
  };

  const handleCancel = () => {
    setSelectedRole(null);
    onClose();
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      style={modalStyles}
      shouldCloseOnOverlayClick
    >
      <ModalContent>
        <Title>{t("set-role")}</Title>

        {formattedRoles.map((role) => (
          <RoleItem
            key={role.id}
            $selected={selectedRole?.name === role?.name}
            $capitalize
            onClick={() => setSelectedRole(role)}
          >
            {t(role?.name)}
          </RoleItem>
        ))}

        <ButtonContainer>
          <Button $variant="cancel" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedRole || addRoleToUserPending}
          >
            {addRoleToUserPending ? (
              <LoadingIcon height={20} width={20} />
            ) : (
              t("save")
            )}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ReactModal>
  );
};

export default RoleModal;
